/*
 * AdDiCtO image strip — auto-scroll hand-off.
 * Loaded only when the section's "Auto-scroll" setting is on. The looping motion
 * itself is a CSS keyframe animation (see section-addicto-image-strip.css); this
 * script only does one thing: the first time the visitor scrolls or swipes the
 * strip, it stops the animation for the rest of the page view WITHOUT a jump by
 * folding the list's current transform offset into the track's scrollLeft.
 */
if (!customElements.get('addicto-image-strip')) {
  customElements.define(
    'addicto-image-strip',
    class AddictoImageStrip extends HTMLElement {
      connectedCallback() {
        if (!this.classList.contains('addicto-image-strip--auto')) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.track = this.querySelector('.addicto-image-strip__track');
        this.list = this.querySelector('.addicto-image-strip__list');
        if (!this.track || !this.list) return;

        this.takeOver = this.takeOver.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        // `scroll` only fires when the track itself moved horizontally (wheel, trackpad,
        // swipe, scrollbar) — unlike wheel/touchstart it ignores vertical page scrolling
        // that merely passes over the strip. It is dispatched before paint, so folding
        // the transform into scrollLeft here is seamless.
        this.track.addEventListener('scroll', this.takeOver, { passive: true });
        this.track.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        this.removeListeners();
      }

      removeListeners() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.takeOver);
        this.track.removeEventListener('keydown', this.onKeydown);
      }

      onKeydown(event) {
        const scrollKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' '];
        if (scrollKeys.includes(event.key)) this.takeOver();
      }

      /* Read the live transform (matrix(a, b, c, d, tx, ty)) → tx is negative while animating */
      currentOffset() {
        const transform = window.getComputedStyle(this.list).transform;
        if (!transform || transform === 'none') return 0;
        const match = transform.match(/matrix\(([^)]+)\)/);
        if (!match) return 0;
        const parts = match[1].split(',').map(parseFloat);
        return Math.abs(parts[4] || 0);
      }

      takeOver() {
        if (this.handedOver) return;
        this.handedOver = true;

        const offset = this.currentOffset();
        // Stop the animation and move the same distance in scroll space, in the same
        // frame, so the visible content does not move.
        this.classList.add('addicto-image-strip--manual');
        this.track.scrollLeft = this.track.scrollLeft + offset;

        this.removeListeners();

        // Scroll snapping stays off (CSS) until the visitor's own scrolling settles,
        // otherwise the browser snaps relative to the gesture and undoes the fold.
        this.restoreSnap = this.restoreSnap.bind(this);
        this.track.addEventListener('scroll', this.restoreSnap, { passive: true });
        this.restoreSnap();
      }

      restoreSnap() {
        window.clearTimeout(this.snapTimer);
        this.snapTimer = window.setTimeout(() => {
          this.track.removeEventListener('scroll', this.restoreSnap);
          this.classList.add('addicto-image-strip--snap');
        }, 250);
      }
    }
  );
}
